import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import lessonData from '../../data/lessons/lesson_ch1_gmr_001_pedagogical.json';
import { TrackingV2Service } from '../TrackingService';

// Unified hook for both Grammar and Vocab modules
export const useTeachModuleState = ({ level, chapter, contentId, isOverlay, onClose, navigation, moduleType = 'grammar' }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(moduleType === 'vocab'); // Grammar is local, Vocab is async
    const [error, setError] = useState(null);
    const [vocabContent, setVocabContent] = useState(null);
    const [grammarContent, setGrammarContent] = useState(null);

    // Timeline/Step State
    const [currentStep, setCurrentStep] = useState(0);
    const [isCompleting, setIsCompleting] = useState(false);

    // Fetch Content
    useEffect(() => {
        const fetchContent = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const db = getFirestore();

                if (moduleType === 'vocab') {
                    // Vocab Fetch logic
                    const docId = `l${level}_ch${chapter}_vocab_teach`;
                    const docPath = `content/teach_vocab/level_${level}/${docId}`;
                    const docRef = doc(db, docPath);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        setVocabContent(docSnap.data());
                    } else {
                        console.error('No vocab teach content found');
                        setError(`Content not generated for Level ${level} Chapter ${chapter}`);
                    }
                } else if (moduleType === 'grammar') {
                    // Grammar Fetch Logic
                    if (contentId) {
                        const docPath = `feature_testing/Korea_University/Level${level}/GrammarTeach/Chapter${chapter}/${contentId}`;
                        const docRef = doc(db, docPath);
                        const docSnap = await getDoc(docRef);

                        if (docSnap.exists()) {
                            setGrammarContent(docSnap.data());
                        } else {
                            console.warn(`Grammar content not found at ${docPath}, using fallback.`);
                            setGrammarContent(lessonData);
                        }
                    } else {
                        // Use static fallback if no ID provided (legacy support)
                        setGrammarContent(lessonData);
                    }
                }
            } catch (err) {
                console.error('Error fetching content:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchContent();
    }, [moduleType, level, chapter, contentId]);

    // Data Source Resolution
    const screens = useMemo(() => {
        if (moduleType === 'grammar') return grammarContent?.steps || grammarContent?.micro_steps || [];
        if (moduleType === 'vocab') return vocabContent?.steps || [];
        return [];
    }, [moduleType, vocabContent, grammarContent]);

    const stageInfo = useMemo(() => {
        if (moduleType === 'grammar') return grammarContent?.lesson_stages || {};
        // Vocab stages are inferred from content type usually, but we can standardize if needed
        return {};
    }, [moduleType, grammarContent]);

    const totalSteps = screens.length;

    // Current Screen
    const currentScreen = useMemo(() => screens[currentStep] || null, [screens, currentStep]);

    // Current Stage Calculation
    const currentStage = useMemo(() => {
        if (moduleType === 'grammar') {
            const stepNum = currentStep + 1;
            for (const [key, stage] of Object.entries(stageInfo)) {
                if (stage.step_range && stepNum >= stage.step_range[0] && stepNum <= stage.step_range[1]) {
                    return { name: stage.stage_name, key };
                }
            }
            return { name: 'Unknown', key: 'unknown' };
        } else {
            // Vocab Logic
            if (!currentScreen) return { key: 'intro', name: 'Loading' };
            if (currentScreen.type === 'intro') return { key: 'intro', name: 'Introduction' };
            if (currentScreen.type === 'summary') return { key: 'conclusion', name: 'Summary' };

            const category = currentScreen.category || 'study';
            return {
                key: category,
                name: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            };
        }
    }, [moduleType, currentStep, stageInfo, currentScreen]);

    // Timeline Data (Sliding Window - 3 Nodes)
    const timelineData = useMemo(() => {
        const prev = currentStep > 0 ? screens[currentStep - 1] : null;
        const current = currentScreen;
        const next = currentStep < totalSteps - 1 ? screens[currentStep + 1] : null;

        const formatLabel = (s) => {
            if (!s) return '';
            if (s.label) return s.label.toUpperCase();
            if (s.type) return s.type.toUpperCase();
            return 'STEP';
        };

        return {
            prev: prev ? { label: formatLabel(prev), id: prev.step_id || 'prev', completed: true } : null,
            current: current ? { label: formatLabel(current), id: current.step_id || 'curr', active: true } : null,
            next: next ? { label: formatLabel(next), id: next.step_id || 'next', disabled: true } : null,
        };
    }, [screens, currentStep, currentScreen, totalSteps]);

    // Navigation Actions
    const handleNext = useCallback(async () => {
        if (currentStep < totalSteps - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            // Completion Logic
            if (isCompleting) return;
            setIsCompleting(true);

            try {
                if (moduleType === 'vocab') {
                    await TrackingV2Service.updateChapterProgress(
                        'user_current',
                        level,
                        chapter,
                        'vocabTeach',
                        { completed: true, timestamp: Date.now() }
                    );
                }
                // Grammar completion logic if needed (usually handled by parent or just navigation)

                if (isOverlay && onClose) {
                    onClose();
                } else if (navigation?.next) {
                    navigation.next();
                } else {
                    navigate(-1); // Fallback
                }

            } catch (err) {
                console.error('Completion error:', err);
            } finally {
                setIsCompleting(false);
            }
        }
    }, [currentStep, totalSteps, isCompleting, moduleType, level, chapter, isOverlay, onClose, navigation, navigate]);

    const handlePrev = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        } else {
            if (isOverlay && onClose) onClose();
            else if (navigation?.previous) navigation.previous();
            else navigate(-1);
        }
    }, [currentStep, isOverlay, onClose, navigation, navigate]);

    const handleJumpToStep = useCallback((index) => {
        if (index >= 0 && index < totalSteps) {
            setCurrentStep(index);
        }
    }, [totalSteps]);

    // Metadata
    const meta = useMemo(() => {
        if (moduleType === 'grammar') {
            const data = grammarContent || lessonData;
            return {
                title: data.title_en || data.title || 'Grammar Lesson',
                titleKr: data.title_kr,
                level: data.level || level,
                chapter: data.chapter || chapter,
                methodology: data.methodology
            };
        } else {
            return vocabContent?.metadata || { title: `Level ${level} Chapter ${chapter}` };
        }
    }, [moduleType, vocabContent, grammarContent, level, chapter]);

    return {
        isLoading,
        error,
        currentStep,
        totalSteps,
        currentScreen,
        currentStage,
        timelineData,
        meta,
        handleNext,
        handlePrev,
        handleJumpToStep
    };
};
